import { useEffect, useState, useCallback } from 'react';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase.js';
import { useAuth } from '../useAuth.js';

// Default shape for each settings doc — used when the Firestore doc doesn't
// exist yet so the UI never has to deal with undefined fields.
export const DEFAULT_BRANDING = {
  logoUrl: '',
  primaryColour: '#00897b',
  accentColour: '#26a69a',
  bgColour: '#f5f7f6',
  fontFamily: 'Arial',
};

export const DEFAULT_FOOTER = {
  companyName: 'Trovex',
  address: '',
  companyNumber: '',
  socialLinks: [],
  copyrightLine: '© Trovex. All rights reserved.',
  unsubscribeWording:
    "You're receiving this because you're a Trovex customer. " +
    "Click below to unsubscribe.",
};

export const DEFAULT_SENDER = {
  displayName: 'Trovex',
  replyTo: 'hello@trovex.com',
  // From is always hello@trovex.com — read-only in UI
};

const EMAIL_SAFE_FONTS = [
  'Arial', 'Helvetica', 'Georgia', 'Verdana', 'Times New Roman', 'system-ui',
];

export { EMAIL_SAFE_FONTS };

const HISTORY_LIMIT = 20;

// Strip transient + metadata fields before stashing a doc into _history —
// otherwise every history entry contains its own _history chain (geometric blowup).
function stripForHistory(data) {
  if (!data) return null;
  const { _history, updatedAt, updatedBy, ...rest } = data;
  return rest;
}

export function useSettings() {
  const { email } = useAuth();
  const [branding, setBranding] = useState(null);
  const [footer, setFooter] = useState(null);
  const [sender, setSender] = useState(null);
  const [loaded, setLoaded] = useState({ branding: false, footer: false, sender: false });

  useEffect(() => {
    const subs = [
      onSnapshot(doc(db, 'eshot_settings', 'branding'), (snap) => {
        setBranding(snap.exists() ? { ...DEFAULT_BRANDING, ...snap.data() } : { ...DEFAULT_BRANDING });
        setLoaded((l) => ({ ...l, branding: true }));
      }),
      onSnapshot(doc(db, 'eshot_settings', 'footer'), (snap) => {
        setFooter(snap.exists() ? { ...DEFAULT_FOOTER, ...snap.data() } : { ...DEFAULT_FOOTER });
        setLoaded((l) => ({ ...l, footer: true }));
      }),
      onSnapshot(doc(db, 'eshot_settings', 'sender'), (snap) => {
        setSender(snap.exists() ? { ...DEFAULT_SENDER, ...snap.data() } : { ...DEFAULT_SENDER });
        setLoaded((l) => ({ ...l, sender: true }));
      }),
    ];
    return () => subs.forEach((u) => u());
  }, []);

  const loading = !(loaded.branding && loaded.footer && loaded.sender);

  const save = useCallback(async (docId, current, patch) => {
    if (!email) throw new Error('Not signed in');
    const ref = doc(db, 'eshot_settings', docId);
    const trimmedHistory = (current?._history || []).slice(-(HISTORY_LIMIT - 1));
    trimmedHistory.push({
      at: Date.now(),
      by: email,
      before: stripForHistory(current),
    });
    const next = {
      ...current,
      ...patch,
      _history: trimmedHistory,
      updatedAt: serverTimestamp(),
      updatedBy: email,
    };
    delete next._historyMeta; // belt-and-braces: never persist any temporary helper keys
    await setDoc(ref, next, { merge: true });
  }, [email]);

  return {
    branding,
    footer,
    sender,
    loading,
    saveBranding: (patch) => save('branding', branding, patch),
    saveFooter: (patch) => save('footer', footer, patch),
    saveSender: (patch) => save('sender', sender, patch),
  };
}
